"use client";

import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/navigation";
import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { SiYoutube } from "react-icons/si";
import { HelpCircle, ExternalLink, Loader2, Copy, Check } from "lucide-react";
import YouTubeVerificationModal from "./YouTubeVerificationModal";
import * as Sentry from "@sentry/nextjs";
import { expireYoutubeSubscription } from "@/lib/server-actions/youtubeActions";
import { isInAppBrowser, copyToClipboard } from "@/lib/inapp-browser";
import { useRefreshSessionClaims } from "@/hooks/useRefreshSessionClaims";

interface YouTubeVerificationProps {
  channelId: string;
  clientId: string;
  onVerificationSuccess: () => void;
  isVerified: boolean;
  onStartVerification?: () => void;
}

// Window 객체에 gapi 추가
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: { access_token: string }) => void;
            error_callback?: (error: { type: string; message: string }) => void;
            ux_mode?: 'popup' | 'redirect';
            prompt?: string;
          }) => {
            requestAccessToken: () => void;
          };
        };
      };
    };
  }
}

export default function YouTubeVerification({
  channelId,
  clientId,
  onVerificationSuccess,
  isVerified,
  onStartVerification,
}: YouTubeVerificationProps) {
  const { isSignedIn, isLoaded, user } = useUser();
  const refreshSessionClaims = useRefreshSessionClaims();
  const router = useRouter();
  const [isVerifyingSubscription, setIsVerifyingSubscription] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [isInApp, setIsInApp] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [verifyUrl, setVerifyUrl] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sessionTokenRef = useRef<string | null>(null);

  // 인앱 브라우저 감지 — navigator 외부 시스템 동기화
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsInApp(isInAppBrowser());
  }, []);

  // YouTube 구독 상태 확인
  useEffect(() => {
    if (isLoaded && isSignedIn && user) {
      const youtubeSubscribed = user.publicMetadata?.youtubeSubscribed as boolean;
      const youtubeSubscribedAt = user.publicMetadata?.youtubeSubscribedAt as string;

      if (youtubeSubscribed && youtubeSubscribedAt) {
        const subscribedDate = new Date(youtubeSubscribedAt);
        const now = new Date();
        const daysDiff = (now.getTime() - subscribedDate.getTime()) / (1000 * 60 * 60 * 24);

        // 30일 이내인지 확인
        if (daysDiff <= 30) {
          onVerificationSuccess();
        } else {
          // 30일 만료: Clerk 메타데이터 업데이트
          expireYoutubeSubscription()
            .then(async () => {
              await user.reload();
              await refreshSessionClaims();
            })
            .catch((error) => {
              console.error("Failed to expire YouTube subscription:", error);
            });
        }
      }
    }
  }, [isLoaded, isSignedIn, user, onVerificationSuccess, refreshSessionClaims]);

  // 폴링 정리
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 툴팁 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTooltip]);

  // 세션 상태 폴링 (인앱 브라우저 플로우)
  const startPolling = useCallback((sessionToken: string) => {
    setIsPolling(true);

    // 기존 폴링 정리
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }

    let pollCount = 0;
    const maxPolls = 120; // 10분 (5초 간격)

    pollingRef.current = setInterval(async () => {
      pollCount++;

      if (pollCount > maxPolls) {
        // 타임아웃
        if (pollingRef.current) clearInterval(pollingRef.current);
        setIsPolling(false);
        setIsVerifyingSubscription(false);
        toast.error("인증 시간이 초과되었습니다. 다시 시도해주세요.");
        return;
      }

      try {
        const response = await fetch(`/api/youtube/check-session?session=${sessionToken}`);
        const data = await response.json();

        if (data.status === "completed") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsPolling(false);
          setIsVerifyingSubscription(false);
          await user?.reload();
          await refreshSessionClaims();
          onVerificationSuccess();
          toast.success("유튜브 구독 인증이 완료되었습니다!");
        } else if (data.status === "not_subscribed") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsPolling(false);
          setIsVerifyingSubscription(false);
          toast.error("정처기 감자 채널을 구독하지 않으신거 같네요! 구독 후 다시 시도해주세요.");
        } else if (data.status === "failed" || data.status === "expired") {
          if (pollingRef.current) clearInterval(pollingRef.current);
          setIsPolling(false);
          setIsVerifyingSubscription(false);
          toast.error("인증에 실패했습니다. 다시 시도해주세요.");
        }
        // "pending"이면 계속 폴링
      } catch {
        // 네트워크 오류 시 폴링 계속 (일시적 오류일 수 있음)
      }
    }, 5000);
  }, [user, onVerificationSuccess, refreshSessionClaims]);

  // 인앱 브라우저: 세션 생성 후 링크 복사 UI 표시
  const performInAppVerification = async () => {
    setIsVerifyingSubscription(true);

    try {
      // 1. 세션 생성
      const response = await fetch("/api/youtube/create-verify-session", {
        method: "POST",
      });
      const data = await response.json();

      if (!data.sessionToken) {
        toast.error("인증 세션 생성에 실패했습니다.");
        setIsVerifyingSubscription(false);
        return;
      }

      sessionTokenRef.current = data.sessionToken;

      // 2. 인증 URL 생성 및 표시
      const url = `${window.location.origin}/youtube-verify?session=${data.sessionToken}`;
      setVerifyUrl(url);

      // 3. 폴링 시작
      startPolling(data.sessionToken);
    } catch (error) {
      console.error("인앱 인증 오류:", error);
      Sentry.captureException(error, {
        tags: { component: 'YouTubeVerification', operation: 'performInAppVerification' },
      });
      toast.error("인증 중 오류가 발생했습니다. 다시 시도해주세요.");
      setIsVerifyingSubscription(false);
    }
  };

  // 링크 복사 핸들러
  const handleCopyLink = async () => {
    if (!verifyUrl) return;
    const success = await copyToClipboard(verifyUrl);
    if (success) {
      setIsCopied(true);
      toast.success("링크가 복사되었습니다! 크롬 또는 사파리에서 붙여넣기 해주세요.");
      setTimeout(() => setIsCopied(false), 3000);
    } else {
      toast.error("링크 복사에 실패했습니다. 직접 복사해주세요.");
    }
  };

  // 모달 관련 함수들
  const handleCloseModal = () => {
    setShowPreviewModal(false);
    setIsVerifyingSubscription(false);
  };

  const handleStartVerification = () => {
    if (onStartVerification) {
      onStartVerification();
    }
    if (isInApp) {
      performInAppVerification();
    } else {
      performYoutubeVerification();
    }
  };

  const performYoutubeVerification = async () => {
    setIsVerifyingSubscription(true);

    try {
      // 1. Google Identity Services 로딩 대기
      await new Promise<void>((resolve, reject) => {
        const checkGoogle = () => {
          if (window.google && window.google.accounts) {
            resolve();
          } else {
            setTimeout(checkGoogle, 100);
          }
        };
        checkGoogle();

        // 10초 타임아웃
        setTimeout(() => {
          reject(new Error("Google Identity Services 로딩 타임아웃"));
        }, 10000);
      });

      // 2. Google OAuth 인증
      const accessToken = await new Promise<string | null>((resolve, reject) => {
        let isResolved = false;

        if (!clientId) {
          reject(new Error("Google Client ID가 설정되지 않았습니다."));
          return;
        }

        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: clientId,
          scope: "https://www.googleapis.com/auth/youtube.readonly",
          ux_mode: 'popup',
          prompt: 'consent',
          callback: (response: { access_token: string }) => {
            if (!isResolved) {
              isResolved = true;
              resolve(response.access_token ?? null);
            }
          },
          error_callback: () => {
            if (!isResolved) {
              isResolved = true;
              // 인증 취소는 정상적인 사용자 행동이므로 null로 resolve
              resolve(null);
            }
          },
        });
        tokenClient.requestAccessToken();

        // 5분 타임아웃 (사용자가 인증을 완료하지 않고 페이지를 닫는 경우)
        setTimeout(() => {
          if (!isResolved) {
            isResolved = true;
            resolve(null);
          }
        }, 300000);
      });

      // 사용자가 인증을 취소한 경우 조용히 종료
      if (accessToken === null) {
        return;
      }

      // 3. 서버에서 구독 검증 및 Clerk 업데이트
      const verifyResponse = await fetch("/api/youtube/verify-subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accessToken }),
      });

      const verifyResult = await verifyResponse.json();

      if (verifyResult.success) {
        await user?.reload();
        await refreshSessionClaims();
        onVerificationSuccess();
      } else if (verifyResult.subscribed === false) {
        toast.error("정처기 감자 채널을 구독하지 않으신거 같네요! 구독 후 다시 시도해주세요.");
      } else {
        toast.error(verifyResult.error || "구독 확인에 실패했습니다.");
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("타임아웃")) {
        console.error("OAuth 인증 타임아웃:", error);
        Sentry.captureException(error, {
          tags: { component: 'YouTubeVerification', operation: 'performYoutubeVerification', errorType: 'timeout' },
          extra: { channelId }
        });
        toast.error("인증 시간이 초과되었습니다. 다시 시도해주세요.");
      } else {
        console.error("YouTube 구독 인증 오류:", error);
        const errorMessage = error instanceof Error ? error.message : "";
        Sentry.captureException(error, {
          tags: { component: 'YouTubeVerification', operation: 'performYoutubeVerification' },
          extra: { channelId, errorMessage }
        });
        if (errorMessage.includes("(401)")) {
          toast.error("Google 인증이 만료되었습니다. 다시 시도해주세요.");
        } else if (errorMessage.includes("(403)")) {
          toast.error("YouTube 인증 서비스가 일시적으로 제한되었습니다. 잠시 후 다시 시도해주세요.");
        } else {
          toast.error("유튜브 구독 인증 중 오류가 발생했습니다. 다시 한 번 시도해주세요.");
        }
      }
    } finally {
      setIsVerifyingSubscription(false);
    }
  };

  const handleYoutubeVerification = async () => {
    if (!isLoaded) {
      return;
    }

    if (!isSignedIn) {
      router.push("/sign-in");
      return;
    }

    if (isInApp) {
      // 인앱 브라우저: 시스템 브라우저로 인증
      if (onStartVerification) {
        onStartVerification();
      }
      performInAppVerification();
    } else {
      // 일반 브라우저: 기존 팝업 플로우
      if (onStartVerification) {
        onStartVerification();
        setTimeout(() => {
          performYoutubeVerification();
        }, 100);
      } else {
        performYoutubeVerification();
      }
    }
  };

  const handleCancelPolling = () => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
    }
    setIsPolling(false);
    setIsVerifyingSubscription(false);
    setVerifyUrl(null);
    setIsCopied(false);
    sessionTokenRef.current = null;
  };

  return (
    <div className="space-y-3">
      {!isVerified && (
        <div className="space-y-1">
          {isPolling ? (
            // 인앱 브라우저: 링크 복사 안내 + 폴링 중 UI
            <div className="space-y-3">
              <div className="w-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 py-4 px-5 rounded-lg space-y-3">
                <div className="text-center space-y-1">
                  <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                    크롬 또는 사파리에서 인증해주세요
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    아래 링크를 복사해서 브라우저 주소창에 붙여넣기 해주세요
                  </p>
                </div>

                {/* 링크 복사 버튼 */}
                <button
                  onClick={handleCopyLink}
                  className="w-full flex items-center justify-center gap-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg py-2.5 px-4 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {isCopied ? (
                    <>
                      <Check className="w-4 h-4 text-green-500" />
                      <span className="text-green-600 dark:text-green-400">복사 완료!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                      <span className="text-gray-700 dark:text-gray-200">인증 링크 복사하기</span>
                    </>
                  )}
                </button>

                {/* 폴링 상태 표시 */}
                <div className="flex items-center justify-center gap-2 pt-1">
                  <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    인증 완료 대기 중... 완료되면 자동으로 반영됩니다
                  </p>
                </div>
              </div>
              <button
                onClick={handleCancelPolling}
                className="w-full text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 py-2 transition-colors"
              >
                취소
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={handleYoutubeVerification}
                disabled={isVerifyingSubscription || !isLoaded}
                className="w-full bg-red-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-red-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <SiYoutube className="w-5 h-5" />
                {isVerifyingSubscription ? "인증 중..." : "구독 인증 하기"}
                {isInApp && !isVerifyingSubscription && <ExternalLink className="w-4 h-4" />}
              </button>
              <a
                href="https://www.youtube.com/@%EC%A0%95%EC%B2%98%EA%B8%B0%EA%B0%90%EC%9E%90"
                target="_blank"
                rel="noopener noreferrer"
                className="block text-center text-xs text-red-500 hover:text-red-600 underline mt-2"
              >
                정처기 감자 유튜브로 이동
              </a>
            </>
          )}
        </div>
      )}

      {/* 안내문구와 툴팁 */}
      {!isVerified && !isPolling && (
        <div className="relative" ref={tooltipRef}>
          <button
            type="button"
            onClick={() => setShowTooltip(!showTooltip)}
            className="mx-auto flex min-h-8 items-center gap-1 rounded-md px-2 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
          >
            {isInApp ? "인앱 브라우저에서 인증이 안 되나요?" : "인증 중 \"액세스 차단됨\" 문구가 나오나요?"}
            <HelpCircle className="w-4 h-4" aria-hidden="true" />
          </button>

          {showTooltip && (
            <div className="absolute top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-sm rounded-lg p-3 w-80 z-50 shadow-lg">
              <div className="text-center leading-relaxed">
                {isInApp ? (
                  <>
                    인앱 브라우저에서는 구글 정책상 직접 인증이 불가하여, 시스템 브라우저(크롬, 사파리)에서 인증이 진행됩니다. 문제가 있으시면{" "}
                  </>
                ) : (
                  <>
                    네이버앱, 인스타앱 등 웹뷰에서는 구글 정책상 인증이 안됩니다. 크롬, 사파리 등 브라우저를 이용하세요. 해당 문제가 아니라면{" "}
                  </>
                )}
                <a
                  href="https://ig.me/m/jeongcheogi_gamja"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-300 hover:text-blue-200 underline"
                >
                  정처기 감자 인스타그램에 DM 주세요
                </a>
              </div>
              {/* 툴팁 화살표 */}
              <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
            </div>
          )}
        </div>
      )}

      {/* 구독 인증 미리보기 모달 */}
      <YouTubeVerificationModal
        isOpen={showPreviewModal}
        onClose={handleCloseModal}
        onStartVerification={handleStartVerification}
      />
    </div>
  );
}
